'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { toasterApi } from '@terra/ui-design-system';
import {
  formatMcpIdentityConnectionFailureDescription,
  testMcpConnectionWithFeedback,
  toMcpIdentityConnectionStatusMap,
  type McpIdentityConnectionStatusMap,
  type McpMultiIdentityDiscoveryResultDto,
} from '@terra/shared-contracts-mcp-servers';
import { useTestMcpConnection } from '../../../hooks/api/useAiApplicationMutations';

export function useMcpConnectionTesting({
  aiApplicationId,
}: {
  aiApplicationId: string;
}) {
  const tAi = useTranslations('aiApplications');
  const testMcpConnectionMutation = useTestMcpConnection({ aiApplicationId });
  const [identityConnectionStatus, setIdentityConnectionStatus] =
    useState<McpIdentityConnectionStatusMap | null>(null);

  const applyConnectionTestResult = (
    result: McpMultiIdentityDiscoveryResultDto,
  ) => {
    setIdentityConnectionStatus(toMcpIdentityConnectionStatusMap(result));
  };

  const mergeConnectionTestResult = (
    result: McpMultiIdentityDiscoveryResultDto,
  ) => {
    setIdentityConnectionStatus((previous) => ({
      ...previous,
      ...toMcpIdentityConnectionStatusMap(result),
    }));
  };

  const showConnectionTestSuccess = (
    result: McpMultiIdentityDiscoveryResultDto,
  ) => {
    const failedIdentities = result.identities.filter(
      (identity) => !identity.connectionSuccess,
    );

    if (failedIdentities.length > 0) {
      toasterApi.warning({
        title: tAi('mcpTest.partialSuccess', {
          connected: result.identities.length - failedIdentities.length,
          total: result.identities.length,
          count: result.tools.length,
        }),
        description: formatMcpIdentityConnectionFailureDescription(
          result.identities,
        ),
      });

      return;
    }

    toasterApi.success({
      title: tAi('mcpTest.success', { count: result.tools.length }),
    });
  };

  const showConnectionTestError = ({
    result,
    description,
  }: {
    result: McpMultiIdentityDiscoveryResultDto | null;
    description?: string;
  }) => {
    if (result) {
      applyConnectionTestResult(result);
    }

    toasterApi.error({
      title: tAi('mcpTest.failed'),
      description: description ?? result?.error ?? undefined,
    });
  };

  const showIdentityConnectionTestSuccess = (
    result: McpMultiIdentityDiscoveryResultDto,
  ) => {
    const failedIdentities = result.identities.filter(
      (identity) => !identity.connectionSuccess,
    );

    if (failedIdentities.length > 0) {
      toasterApi.error({
        title: tAi('mcpTest.failed'),
        description: formatMcpIdentityConnectionFailureDescription(
          result.identities,
        ),
      });

      return;
    }

    toasterApi.success({
      title: tAi('mcpTest.identitySuccess', { count: result.tools.length }),
    });
  };

  const testConnection = () => testMcpConnectionMutation.mutateAsync({});

  const handleTestConnection = () => {
    setIdentityConnectionStatus(null);
    void testMcpConnectionWithFeedback({
      testConnection,
      onResult: applyConnectionTestResult,
      onSuccess: showConnectionTestSuccess,
      onError: showConnectionTestError,
    });
  };

  const handleTestIdentity = (identityId: string) => {
    void testMcpConnectionWithFeedback({
      testConnection: () =>
        testMcpConnectionMutation.mutateAsync({ identityId }),
      onResult: mergeConnectionTestResult,
      onSuccess: showIdentityConnectionTestSuccess,
      onError: ({ result, description }) => {
        if (result) {
          mergeConnectionTestResult(result);
        }

        toasterApi.error({
          title: tAi('mcpTest.failed'),
          description: description ?? result?.error ?? undefined,
        });
      },
    });
  };

  return {
    identityConnectionStatus,
    isTestPending: testMcpConnectionMutation.isPending,
    pendingTestIdentityId: testMcpConnectionMutation.isPending
      ? (testMcpConnectionMutation.variables?.identityId ?? null)
      : null,
    isBulkConnectionTestPending:
      testMcpConnectionMutation.isPending &&
      testMcpConnectionMutation.variables?.identityId == null,
    testConnection,
    applyConnectionTestResult,
    showConnectionTestSuccess,
    showConnectionTestError,
    handleTestConnection,
    handleTestIdentity,
  };
}
